// Tsukinoura app: screens, turn flow, and the render loop.
import i18next from 'i18next';
import * as THREE from 'three';
import './style.css';
import { resources } from './locales.js';
import { Stage } from './game/stage.js';
import { SpriteBank } from './game/sprites.js';
import { CharacterBank, applyKageeWorld, updateKageeLanterns, addForegroundFrame, layoutForeground } from './game/kagee.js';
import { PoolWater } from './game/water.js';
import { Fx } from './game/fx.js';
import { Audio, vibrate } from './game/audio.js';
import { Aim } from './game/aim.js';
import { PoolView } from './game/view.js';
import { startNight, playTurn, nextPool, nightTotals } from './sim/run.js';
import { darkForecast, bossWater, openCells, stencilProgress } from './sim/pool.js';
import { cellAt } from './sim/grid.js';
import { nightPlan, nightInfo } from './sim/night.js';
import { BASINS } from './sim/basins.js';
import { CRABS, TUNE } from './sim/content.js';
import { renderNight } from './meta/glass.js';
import { planTurn, humanNoise, PARTNER } from './sim/bot.js';
import { h, moonIcon } from './ui/dom.js';
import { createScreens } from './ui/screens.js';
import { loadProfile, saveProfile, recordNight, addFinds } from './meta/profile.js';
import { beachFinds } from './meta/beach.js';
import { moonriseMinutes, onMoonRoad, hhmm } from './meta/moonrise.js';
import { CoveView, COVE_CAMERA } from './game/cove.js';
import { SHELLS } from './sim/shells.js';
import { rand } from './sim/rand.js';

const params = new URLSearchParams(location.search);
const pad = n => String(n).padStart(2, '0');
const localDate = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const TODAY = params.get('date') || localDate(new Date());
const reducedMotionPref = matchMedia('(prefers-reduced-motion: reduce)').matches;

const store = {
  get(k, d) { try { const v = localStorage.getItem('tsukinoura:' + k); return v == null ? d : JSON.parse(v); } catch { return d; } },
  set(k, v) { try { localStorage.setItem('tsukinoura:' + k, JSON.stringify(v)); } catch { /* private mode */ } },
};

await i18next.init({
  lng: params.get('lang') || ((navigator.language || 'en').startsWith('ja') ? 'ja' : 'en'),
  fallbackLng: 'en', resources, interpolation: { escapeValue: true },
});
const t = (k, o) => i18next.t(k, o);
document.documentElement.lang = i18next.language;

const canvas = document.getElementById('view');
const ui = document.getElementById('ui');
const stage = new Stage(canvas);
const coarse = matchMedia('(pointer: coarse)').matches;
stage.setQuality(params.get('quality') || (coarse || innerWidth < 760 ? 'low' : 'high'));
// Look: 'kagee' (shadow-play, 3D characters) or 'hd2d' (pixel sprites).
const LOOK = params.get('look') || 'kagee';
PoolWater.kagee = LOOK === 'kagee';
stage.turnable = LOOK === 'kagee';   // 3D puppets can be seen from any side; pixel sprites only from the front
document.body.classList.add('look-' + LOOK);
const sprites = LOOK === 'kagee' ? new CharacterBank() : new SpriteBank();
const audio = new Audio();
const fx = new Fx(stage.scene, stage);
fx.kagee = LOOK === 'kagee';
const aim = new Aim(stage, canvas);

const app = {
  screen: 'loading', night: null, run: null, view: null, inputs: [], seats: store.get('seats', ['human', 'bot', 'bot', 'bot']),
  aimOrder: [], aimIndex: 0, practice: false, motion: store.get('motion', !reducedMotionPref), sound: store.get('sound', true),
  speed: 1, transition: null, endShown: false,
};
window.__tsuki = { app, stage, aim, audio, Audio };
const profile = loadProfile();
const saveP = () => saveProfile(profile);
let cove = null;
const LANTERN_GOAL = 1000, LANTERN_SAMPLE = 999;
const screens = createScreens({
  ui, t, profile, save: saveP, today: TODAY, audio, lanternGoal: LANTERN_GOAL, lanternSample: LANTERN_SAMPLE,
  home: () => { app.titleSnapped = false; showTitle(); },
  startMeguri: (date, shells) => begin('meguri', { date, shells }),
  enterCove: () => {
    app.screen = 'cove';
    if (!cove) cove = new CoveView(stage, sprites, profile, LOOK === 'kagee');
    Object.assign(stage.goal, { dist: COVE_CAMERA.dist, pitch: COVE_CAMERA.pitch, yaw: COVE_CAMERA.yaw, fov: COVE_CAMERA.fov });
    stage.goal.target.copy(COVE_CAMERA.target); stage.camSpeed = 1.2;
    // Portrait: the panel takes the lower half, so look down a little more and
    // aim lower, keeping the houses and lanterns in the top half.
    if (stage.aspect < 0.8) { stage.goal.pitch += THREE.MathUtils.degToRad(10); stage.goal.fov = 42; stage.goal.target.y -= 3; }
  },
  coveChanged: () => cove?.rebuild(profile, sprites),
  leaveCove: () => { cove?.dispose(); cove = null; },
});
Object.assign(window.__tsuki, { profile, screens });
// 灯の加護: this month's lanterns (a stand-in count for everyone else, plus yours) reached the goal.
const wardOn = () => LANTERN_SAMPLE + profile.lanterns.filter(l => l.month === TODAY.slice(0, 7)).length >= LANTERN_GOAL;

// Bot planning in a worker (falls back to the main thread).
let worker = null, jobId = 0;
try { worker = new Worker('/assets/bot-worker.js', { type: 'module' }); } catch { worker = null; }
function planBots(pool, fixed, crabs) {
  if (!crabs.length) return Promise.resolve(fixed);
  const seed = (app.run.plan.seed ^ Math.imul(app.run.turnsUsed + 1, 0x9e3779b1)) >>> 0;
  if (!worker) return Promise.resolve(planTurn(pool, fixed, crabs, { dirs: PARTNER.dirs, powers: PARTNER.powers, noise: humanNoise(rand(seed), PARTNER.deg, PARTNER.pct) }));
  const id = ++jobId;
  return new Promise(resolve => {
    const onMsg = e => { if (e.data.id === id) { worker.removeEventListener('message', onMsg); resolve(e.data.inputs); } };
    worker.addEventListener('message', onMsg);
    worker.postMessage({ id, pool, fixed, crabs, seed });
  });
}

// Baked, verified layouts for known dates (optional).
async function bakedAttempts(iso) {
  try {
    const res = await fetch(`/nights/${iso.slice(0, 4)}.json`);
    if (!res.ok) return [];
    const table = await res.json();
    return table[iso]?.attempts || [];
  } catch { return []; }
}

// ------------------------------------------------------------------ DOM helpers
// ------------------------------------------------------------------ title
function showTitle() {
  app.screen = 'title';
  audio.setMode?.('title');
  const info = app.night;
  const [y, m, d] = TODAY.split('-');
  const record = store.get('record:' + TODAY, null);
  ui.replaceChildren(h('div', { class: 'title-screen' },
    h('div', { class: 'title-block' },
      h('div', { class: 'title-head' },
        h('div', {}, h('h1', { class: 'title' }, t('title')), h('div', { class: 'subtitle' }, t('subtitle'))),
        h('div', { class: 'title-moon' }, moonIcon(info.phase, 52))),
      h('div', { class: 'night' },
        h('div', { class: 'night-date' }, t('date', { y, m: +m, d: +d }), h('span', { class: 'night-tide' }, ' ' + t('tide.' + info.tide)), h('span', { class: 'dim' }, ' · ' + t('moonAge', { age: info.lunarDay - 1 }))),
        h('p', { class: 'night-line' }, t('tideLine.' + info.tide))),
      h('div', { class: 'game-of-night' }, h('div', { class: 'okite-label' }, t('gameOfNight')),
        h('div', { class: 'gon-name' }, t('mode.' + info.mode)), h('div', { class: 'gon-desc' }, t('howgame.' + info.mode))),
      h('div', { class: 'okite' }, h('div', { class: 'okite-label' }, t('okite')),
        h('div', { class: 'rules' }, info.okite.rules.map(id => h('div', { class: `rule rule-${id}`, title: t('rule.' + id + '.desc') },
          h('span', { class: 'rule-name' }, t('rule.' + id + '.name')), h('span', { class: 'rule-desc short' }, t('rule.' + id + '.desc')))))),
      h('div', { class: `moonrise ${onMoonRoad(TODAY) ? 'on' : ''}` }, onMoonRoad(TODAY) ? t('moonroad.on') : t('moonroad.next', { time: hhmm(moonriseMinutes(TODAY)) })),
      record ? h('div', { class: 'record' }, `${t('nightGoal.' + (record.game || info.mode))} ${goalCount(record.game || info.mode, record)}`) : null),
    h('div', { class: 'title-foot' },
      h('div', { class: 'crew-row' }, h('span', { class: 'crew-label' }, t('crew')),
        CRABS.map((c, i) => h('button', {
          class: `seat-chip seat-${i} ${app.seats[i]}`, 'data-seat': i, title: t('shell.' + c.id),
          onclick: () => { if (i === 0) return; app.seats[i] = app.seats[i] === 'human' ? 'bot' : 'human'; store.set('seats', app.seats); audio.ui(); showTitle(); },
        }, t('crab.' + c.id), h('span', { class: 'seat-who' }, t('seat.' + app.seats[i]))))),
      h('button', { id: 'start', class: 'primary', onclick: () => begin(record ? 'practice' : 'daily') }, record ? t('practice') : t('start')),
      // Menus appear as there is something behind them: nothing before the
      // first night, then the window and the cove; the rest sit under その他.
      profile.nights.length ? h('div', { class: 'nav' },
        h('button', { class: 'nav-btn', onclick: () => screens.showGallery() }, t('nav.gallery')),
        h('button', { class: 'nav-btn', onclick: () => screens.showCove() }, t('nav.cove')),
        app.navOpen ? [
          h('button', { class: 'nav-btn', onclick: () => screens.showMeguri() }, t('nav.meguri')),
          h('button', { class: 'nav-btn', onclick: () => screens.showShells() }, t('nav.shells')),
          h('button', { class: 'nav-btn', onclick: () => screens.showAlmanac() }, t('nav.almanac')),
          h('button', { class: 'nav-btn', onclick: () => screens.showShop() }, t('nav.shop'))] : null,
        h('button', { class: 'nav-btn nav-more', onclick: () => { app.navOpen = !app.navOpen; audio.ui(); showTitle(); } }, t(app.navOpen ? 'nav.less' : 'nav.more'))) : null),
    h('div', { class: `corner ${app.settingsOpen ? 'open' : ''}` },
      h('button', { class: 'chip settings-toggle', onclick: () => { app.settingsOpen = !app.settingsOpen; showTitle(); } }, t('settings.title')),
      h('button', { class: 'chip', onclick: () => { const lng = i18next.language === 'ja' ? 'en' : 'ja'; params.set('lang', lng); history.replaceState(null, '', '?' + params); i18next.changeLanguage(lng); document.documentElement.lang = lng; showTitle(); } }, t('lang')),
      h('button', { class: 'chip', onclick: () => { app.sound = !app.sound; store.set('sound', app.sound); audio.enabled = app.sound; showTitle(); } }, `${t('settings.sound')}: ${t(app.sound ? 'settings.on' : 'settings.off')}`),
      h('button', { class: 'chip', onclick: () => { app.motion = !app.motion; store.set('motion', app.motion); showTitle(); } }, `${t('settings.motion')}: ${t(app.motion ? 'settings.on' : 'settings.off')}`)),
    h('div', { class: 'art-note' }, t('art')),
  ));
  titleCamera();
}

// Point the camera rig the way a Cam_* empty looks (its extras.target if any).
function aimRigAt(cam, reach) {
  const p = cam.obj.getWorldPosition(new THREE.Vector3());
  const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.obj.getWorldQuaternion(new THREE.Quaternion()));
  const tgt = cam.obj.userData?.target;
  const target = tgt ? new THREE.Vector3(...tgt) : p.clone().addScaledVector(fwd, reach);
  const off = p.clone().sub(target);
  Object.assign(stage.goal, { dist: off.length(), pitch: Math.asin(off.y / off.length()), yaw: Math.atan2(off.x, off.z), fov: cam.fov });
  stage.goal.target.copy(target);
}

function titleCamera() {
  const cam = stage.cams?.Cam_Title;
  if (cam) aimRigAt(cam, 40);
  else {
    stage.goal.target.set(30, 0, -10); stage.goal.dist = 95; stage.goal.pitch = THREE.MathUtils.degToRad(16); stage.goal.yaw = THREE.MathUtils.degToRad(-20); stage.goal.fov = 34;
  }
  if (app.screen === 'title' && !app.titleSnapped) { Object.assign(stage.rig, { dist: stage.goal.dist, pitch: stage.goal.pitch, yaw: stage.goal.yaw, fov: stage.goal.fov }); stage.rig.target.copy(stage.goal.target); app.titleSnapped = true; }
  stage.camSpeed = 0.8;
}

// ------------------------------------------------------------------ night flow
// mode: 'daily' (recorded), 'practice' (tonight again), 'meguri' (any night, your shells).
async function begin(mode, { date = TODAY, shells = null } = {}) {
  audio.start(); audio.enabled = app.sound; audio.ui();
  app.mode = mode;
  app.practice = mode !== 'daily';
  app.date = date;
  const attempts = date === TODAY ? app.attempts : await bakedAttempts(date);
  // ?okite=current,vortex forces tonight's rules (QA only).
  const forced = params.get('okite') ? { rules: params.get('okite').split(','), params: { currentDir: [707, 707] } } : null;
  const lanterns = profile.lanterns.filter(l => l.month === TODAY.slice(0, 7)).map(l => l.name);
  app.run = startNight(date, attempts, { shells, ward: wardOn(), okite: forced, mode: params.get('mode'), lanterns });
  stage.setMoon(app.run.plan.light, app.run.plan.lunarDay);
  app.startedAt = performance.now();
  app.endShown = false;
  setTide(true);
  openPool(true);
}

function openPool(first) {
  const run = app.run;
  const old = app.view;
  app.view = new PoolView({ stage, sprites, fx, audio, pool: run.pool, t, pattern: profile.patternOn || '', phase: run.plan.phase });
  for (const [id, m] of Object.entries(stage.basinWater || {})) m.visible = id !== run.pool.basin;
  for (const h of run.history) if (stage.basinWater?.[h.basin]) stage.basinWater[h.basin].material.uniforms.lit.value = h.total ? 0.25 + 0.75 * (h.saved / h.total) : 1;
  const basin = BASINS.find(b => b.id === run.pool.basin);
  stage.framePool(basin);
  // Portrait screens can't show the shelf sideways: dip to black instead of panning.
  if (innerWidth / innerHeight < 0.8 && !first) { stage.panTo(0.01); stage.fadeIn = 1; }
  else stage.panTo(reducedMotionPref ? 0.01 : first ? 2.6 : 1.8);
  audio.setMode?.(run.pool.boss ? 'boss' : 'pool');
  // Crabs scuttle in from the shore side.
  app.view.crabs.forEach((a, i) => { a.play('walk'); a.entering = { from: -3.5 - i * 0.6, t: 0, dur: first ? 2.4 : 1.8 }; });
  if (old) setTimeout(() => old.dispose(), 1600);
  app.screen = 'enter';
  app.enterT = first ? 3.8 : 2.0;   // the first pool also shows tonight's card
  // Weather of tonight's rules.
  audio.rain?.(!!run.pool.rules.rain);
  document.body.classList.toggle('fog', !!run.pool.rules.fog);
  document.body.classList.toggle('rain', !!run.pool.rules.rain);
  renderHud();
}

function startTurn() {
  const run = app.run;
  app.inputs = [];
  app.aimOrder = [0, 1, 2, 3].filter(i => app.seats[i] === 'human');
  app.aimIndex = 0;
  // Fog hides tomorrow's dark, unless a crab wears the nautilus.
  const seeThrough = run.pool.crabs.some(c => SHELLS[c.shell]?.trait === 'foresight');
  const forecast = run.pool.rules.fog && !seeThrough ? [] : darkForecast(run.pool);
  app.view.showForecast(forecast);
  const fset = new Set(forecast);
  app.view.setDanger(new Set(run.pool.littles.map((l, i) => (l.state === 'lost' && fset.has(cellAt(run.pool.grid, l.x, l.y)) ? i : -1)).filter(i => i >= 0)));
  audio.setTension?.(run.tideLeft <= 3 ? 1 : 0);
  nextAim();
}

function nextAim() {
  const run = app.run;
  if (app.aimIndex >= app.aimOrder.length) { aim.end(); app.view.mark(-1); resolveTurn(); return; }
  const id = app.aimOrder[app.aimIndex];
  app.screen = 'aim';
  const view = app.view;
  view.crabs.forEach((a, i) => { a.uniforms.glow.value.setRGB(0, 0, 0); if (i !== id && a.anim !== 'stuck') a.play('idle'); });
  view.crabs[id].uniforms.glow.value.setRGB(0.1, 0.09, 0.06);
  view.mark(id);
  aim.begin(run.pool, view.centre, id, {
    locked: app.inputs,
    onPower: (p, detent) => {
      if (detent) { audio.pullDetent(); vibrate(8); return; }
      if (!audio.pull) audio.pullStart();
      audio.pullUpdate(p);
      const a = view.crabs[id];
      a.hold('pull', p < 0.34 ? 0 : p < 0.7 ? 1 : 2);
      // Lean back along the pull, squash as it loads, shiver when full.
      const d = aim.drag, l = Math.hypot(d.dx, d.dz) || 1;
      const c = run.pool.crabs[id];
      view.place(a, c.x, c.y);
      a.group.position.x -= (d.dx / l) * 0.14 * p;
      a.group.position.z -= (d.dz / l) * 0.14 * p;
      if (p >= 1) { a.group.position.x += (Math.random() - 0.5) * 0.03; a.group.position.z += (Math.random() - 0.5) * 0.03; }
      a.scale.set(1 + 0.1 * p, 1 - 0.1 * p);
    },
    onCancel: () => {
      audio.pullEnd(); view.crabs[id].release(); view.crabs[id].play('idle');
      const c = run.pool.crabs[id]; view.place(view.crabs[id], c.x, c.y);
    },
    onLock: input => {
      audio.pullEnd();
      const a = view.crabs[id];
      a.release();
      a.hold('pull', 2);
      // The finger's own snap, right now: a click, a shudder, a nudge of the camera.
      audio.release(input.power / 1000, 0.55);
      vibrate(10);
      a.scale.set(0.8, 1.2);
      const c = run.pool.crabs[id];
      view.place(a, c.x, c.y);
      if (app.motion) kickAlong([input], 0.035);
      app.inputs.push(input);
      app.aimIndex++;
      nextAim();
    },
  });
  renderHud();
}

// Nudge the camera against the mean launch direction (screen space).
function kickAlong(inputs, amount) {
  if (!inputs.length) return;
  const v = app.view, cam = stage.camera;
  let sx = 0, sy = 0;
  for (const i of inputs) {
    const c = app.run.pool.crabs[i.crab];
    const a = v.world(c.x, c.y), b = a.clone().add(new THREE.Vector3(i.dx / 1000, 0, i.dy / 1000));
    const pa = a.project(cam), pb = b.project(cam);
    sx += pb.x - pa.x; sy += pb.y - pa.y;
  }
  const l = Math.hypot(sx, sy) || 1;
  stage.kick(-(sx / l) * amount, -(sy / l) * amount);
}

function undoAim() {
  if (app.screen !== 'aim' || app.aimIndex === 0) return;
  app.aimIndex--;
  const id = app.aimOrder[app.aimIndex];
  app.inputs = app.inputs.filter(i => i.crab !== id);
  app.view.crabs[id].release(); app.view.crabs[id].play('idle');
  aim.end();
  nextAim();
}

async function resolveTurn() {
  const run = app.run;
  const bots = [0, 1, 2, 3].filter(i => app.seats[i] !== 'human');
  app.screen = 'thinking';
  renderHud();
  const started = performance.now();
  const inputs = await planBots(run.pool, app.inputs, bots);
  // Partners reveal their aims one by one while every launching crab ducks in;
  // then all four let go together.
  const view = app.view;
  const shown = [...app.inputs];
  let beat = 0;
  for (const input of inputs.filter(i => !app.inputs.includes(i))) {
    await new Promise(r => setTimeout(r, Math.max(140, 300 - (performance.now() - started) / 4)));
    shown.push(input);
    aim.drawGhosts(shown);
    view.crabs[input.crab].hold('pull', 2);
    view.crabs[input.crab].scale.set(0.85, 1.15);
    audio.countIn(beat++);
  }
  for (const input of inputs) view.crabs[input.crab].hold('pull', 2);
  // One more beat, then everyone lets go together.
  await new Promise(r => setTimeout(r, 200));
  if (inputs.length > 1) audio.countIn(3);
  await new Promise(r => setTimeout(r, 160));
  aim.clearGhosts();
  for (const a of view.crabs) a.release();
  const { res, run: next } = playTurn(run, inputs);
  app.run = next;
  app.screen = 'resolve';
  app.speed = 1;
  setTide();
  app.view.showForecast([]);
  // All shells leave together: a staggered snap per launching crab.
  res.events.filter(e => e.type === 'launch').forEach((e, k) => setTimeout(() => audio.release(Math.min(1, e.speed / 751)), k * 22));
  vibrate(14);
  if (app.motion) kickAlong(inputs, 0.06);
  app.saveInputs = inputs;
  renderHud();
  app.view.play(res, {
    speed: 1,
    onEvent: e => {
      if (e.type === 'pen' && e.cells.length > 40) vibrate([15, 30, 25]);
      if (e.type === 'saved' || e.type === 'taken' || e.type === 'pen') renderHud(res.pool);
    },
    onDone: () => afterTurn(),
  });
}

function afterTurn() {
  const run = app.run;
  renderHud();
  if (run.poolCleared || run.over) {
    const lastPool = run.history.at(-1);
    const all = lastPool && (lastPool.picture != null ? lastPool.picture >= TUNE.stencilGoal : lastPool.total > 0 && lastPool.saved === lastPool.total);
    if (run.poolCleared) {
      audio.poolClear();
      app.view.crabs.forEach(a => a.play('cheer', { then: 'idle' }));
      banner(lastPool.calm ? t('event.calm') : all ? t('allDone.' + (lastPool.mode || 'pen')) : t('event.clear'));
      app.view.water.material.uniforms.calm.value = 0;
    } else banner(t('event.tideBack'));
    app.screen = 'cleared';
    setTimeout(() => {
      if (run.over) showEnd();
      else { app.run = nextPool(run); openPool(false); }
    }, run.over && !run.poolCleared ? 1800 : 2300);
    return;
  }
  startTurn();
}

function banner(text) {
  const el = h('div', { class: 'banner' }, text);
  ui.append(el);
  setTimeout(() => el.classList.add('out'), 1700);
  setTimeout(() => el.remove(), 2400);
}

// ------------------------------------------------------------------ HUD
// The old one's water as a share of the pool: fence it down to the line and it sleeps.
function bossGauge(pool) {
  const pct = pool.boss.calm ? 0 : Math.round((bossWater(pool) * 100) / openCells(pool.grid));
  const goal = Math.round(TUNE.bossCalmPermille / 10);
  return h('div', { class: `boss-gauge ${pool.boss.calm ? 'calm' : ''}` },
    h('div', { class: 'boss-label' }, pool.boss.calm ? t('event.calm') : t('hud.bossWater', { goal })),
    h('div', { class: 'boss-bar' }, h('div', { class: 'boss-fill', style: `width:${pct}%` }), h('div', { class: 'boss-goal', style: `left:${goal}%` })),
    pool.boss.calm ? null : h('div', { class: 'boss-num' }, `${pct}%`));
}

// One-line coaching on the first two turns of the night, until the player has a record.
function coachLine() {
  const run = app.run;
  if (run.index !== 0 || run.pool.turn > 1 || store.get('coached', false)) return null;
  if (run.pool.turn === 1) store.set('coached', true);
  const mode = run.pool.mode || 'pen';
  return h('div', { class: 'coach' }, t(run.pool.turn === 0 ? `coachFirst.${mode}` : `howgame2.${mode}`));
}

// The sea climbs as the night's tide budget runs out.
function setTide(instant = false) {
  const run = app.run;
  const k = 1 - Math.max(0, run.tideLeft) / run.tideMax;
  stage.tideGoal = -0.95 + 0.8 * k;
  if (instant) stage.sea.position.y = stage.tideGoal;
}

function renderHud(poolOverride) {
  if (!['enter', 'aim', 'thinking', 'resolve', 'cleared'].includes(app.screen)) return;
  const run = app.run;
  const pool = poolOverride || run.pool;
  // While the clearing turn plays back, history already holds this pool: count it once.
  const past = run.poolCleared ? run.history.slice(0, -1) : run.history;
  const savedSoFar = past.reduce((n, p) => n + p.saved, 0) + pool.littles.filter(l => l.state === 'saved').length;
  const poolName = t('pool.' + pool.basin);
  // The how-to lines show only while coaching; after that the prompt is one line.
  const coach = app.screen === 'aim' ? coachLine() : null;
  const prompt = app.screen === 'aim'
    ? h('div', { class: 'prompt' },
        h('div', { class: `who who-${app.aimOrder[app.aimIndex]}` }, t('hud.aim', { name: t('crab.' + CRABS[app.aimOrder[app.aimIndex]].id) })),
        app.view.danger?.size ? h('div', { class: 'danger-line' }, t('danger', { n: app.view.danger.size })) : null,
        coach ? h('div', { class: 'hint' }, t('hud.aimHint')) : null,
        coach)
    : app.screen === 'thinking' ? h('div', { class: 'prompt quiet' }, t('hud.thinking'))
    : app.screen === 'resolve' ? h('div', { class: 'prompt quiet small' }, t('hud.skip')) : null;
  ui.replaceChildren(
    h('div', { class: 'hud' },
      h('div', { class: 'hud-left' },
        h('div', { class: 'pool-name' }, poolName),
        h('div', { class: 'pool-dots' }, run.plan.basins.map((b, i) => h('span', { class: `dot ${i < run.index ? 'done' : i === run.index ? 'now' : ''} ${b === 'fuchi' ? 'fuchi' : ''}` })))),
      h('div', { class: `hud-tide ${run.tideLeft <= 3 ? 'low' : ''}` },
        h('div', { class: 'tide-label' }, t('hud.tide')),
        h('div', { class: 'tide-pips' }, Array.from({ length: run.tideMax }, (_, i) => h('span', { class: `tide-pip ${i < Math.max(0, run.tideLeft) ? '' : 'gone'}` }))),
        h('div', { class: 'tide-num' }, t('hud.turns', { n: Math.max(0, run.tideLeft) }))),
      h('div', { class: 'hud-right' },
        h('div', { class: 'saved-label' }, t('goal.' + (pool.mode || 'pen'))),
        pool.stencil
          ? h('div', { class: 'saved-num' }, `${Math.round(stencilProgress(pool).permille / 10)}%`, h('span', { class: 'dim' }, ` / ${pool.stencil.goal / 10}%`))
          : h('div', { class: 'saved-num' }, `${pool.littles.filter(l => l.state === 'saved').length}`, h('span', { class: 'dim' }, ` / ${pool.littles.length}`)),
        ...(pool.stencil ? [] : [
          h('div', { class: 'littles' }, pool.littles.map(l => h('span', { class: `lt ${l.state}` }))),
          h('div', { class: 'night-total' }, t('hud.nightSaved', { n: savedSoFar, total: run.totalLittles }))]))),
    ...(pool.boss ? [bossGauge(pool)] : []),
    h('div', { class: 'hud-rules' }, screens.rulesLine(run.plan.okite)),
    // Tonight's card: the game and the rules, once, while the crabs walk in.
    ...(app.screen === 'enter' && run.index === 0 ? [h('div', { class: 'rule-card' },
      h('div', { class: 'rc-label' }, t('gameOfNight')),
      h('div', { class: 'rc-mode' }, t('mode.' + (pool.mode || 'pen'))),
      run.plan.okite.rules.length ? [h('div', { class: 'rc-label' }, t('okite')), screens.rulesLine(run.plan.okite, { long: true })] : null)] : []),
    h('div', { class: 'bottom' },
      prompt,
      app.screen === 'aim' ? h('div', { class: 'seats-row' }, CRABS.map((c, i) => h('span', {
        class: `chip-seat seat-${i} ${app.inputs.some(x => x.crab === i) ? 'locked' : app.aimOrder[app.aimIndex] === i ? 'now' : ''}`,
      }, t('crab.' + c.id), ' ', app.seats[i] === 'bot' ? h('span', { class: 'dim' }, t('seat.bot')) : app.inputs.some(x => x.crab === i) ? '✓' : ''))) : null,
      app.screen === 'aim' && app.aimIndex > 0 ? h('button', { class: 'chip undo', onclick: undoAim }, t('hud.back')) : null),
  );
}

// ------------------------------------------------------------------ end
function resetShelf() {
  app.view?.dispose(); app.view = null;
  audio.rain?.(false);
  document.body.classList.remove('fog', 'rain');
  stage.dawn = 0; stage.tideSpeed = 0.25; stage.tideGoal = -0.95;
  stage.sea.material.uniforms.clarity.value = 0;
  for (const m of Object.values(stage.basinWater || {})) { m.visible = true; m.material.uniforms.lit.value = 0; }
}

// The night's count in the words of its game: a share of the picture, or n / total.
function goalCount(mode, totals) {
  if (mode === 'picture') return totals.picture >= 0 ? `${Math.round(totals.picture / 10)}%` : '-';
  return `${totals.saved} / ${totals.total}`;
}

function shareText(totals) {
  const moons = ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'];
  const full = { pen: '🟦', stars: '⭐', lantern: '🏮', floats: '🫧', picture: '🖼' }[app.run.plan.mode] || '🟦';
  const marks = app.run.history.map(p => (p.calm ? '🐙' : p.picture != null ? (p.picture >= 800 ? full : '🔸') : p.saved === p.total ? full : p.saved > 0 ? '🔹' : '⬛'));
  while (marks.length < app.run.plan.basins.length) marks.push('⬛');
  return [
    `${t('title')} ${app.date} ${moons[app.run.plan.phase]}${t('tide.' + app.run.plan.tide)} · ${t('mode.' + app.run.plan.mode)}`,
    `${marks.join('')}  ${t('nightGoal.' + app.run.plan.mode)} ${goalCount(app.run.plan.mode, totals)}`,
    `${t('end.tideLeft')} ${totals.tideLeft}${app.run.plan.mode === 'pen' ? ` · ${t('end.pens')} ${totals.pens}` : ''}${totals.allFour ? ` · ${t('end.four')} ${totals.allFour}` : ''}`,
  ].join('\n');
}

function showEnd() {
  const run = app.run;
  const totals = nightTotals(run);
  if (app.mode === 'daily' && !store.get('record:' + TODAY, null)) store.set('record:' + TODAY, { ...totals, game: app.run.plan.mode, at: Date.now() });
  app.screen = 'end';
  // Keep the night (residents, glass) and work out the dawn beach.
  recordNight(profile, { date: app.date, mode: app.mode, plan: run.plan, totals, history: run.history });
  profile.pearls += totals.pearls;
  let beach = null;
  if (app.mode !== 'practice') {
    const n = app.mode === 'daily' ? 0 : Object.keys(profile.beach).filter(k => k.startsWith(`${app.date}:meguri`)).length;
    const key = `${app.date}:${app.mode}:${n}`;
    if (!profile.beach[key]) {
      const lucky = run.pool.crabs.some(c => SHELLS[c.shell]?.trait === 'lucky');
      beach = beachFinds({ date: app.date, mode: app.mode, n, tide: run.plan.tide, rules: run.plan.okite.rules, saved: totals.saved, total: totals.total, lucky, moonRoad: onMoonRoad(TODAY) });
      profile.beach[key] = beach;
      addFinds(profile, beach);
    }
  }
  saveP();
  app.lastNight = profile.nights[0];
  aim.end();
  // Leave the last pool's water to the shelf view, lit by how it went.
  const lastH = run.history.at(-1);
  if (lastH && stage.basinWater?.[lastH.basin]) { stage.basinWater[lastH.basin].material.uniforms.lit.value = lastH.total ? 0.25 + 0.75 * (lastH.saved / lastH.total) : 1; stage.basinWater[lastH.basin].visible = true; }
  if (app.view) app.view.group.visible = false;
  audio.setTension?.(0);
  const cam = stage.cams?.Cam_End;
  if (cam) aimRigAt(cam, 50); else { stage.goal.dist *= 1.8; stage.goal.pitch = THREE.MathUtils.degToRad(30); }
  stage.camSpeed = 0.5;
  // The tide floods back over the shelf; at dawn the sky pales as well.
  stage.tideGoal = 0.35;
  stage.tideSpeed = 0.06;
  stage.sea.material.uniforms.clarity.value = 1;
  if (run.reason === 'dawn') stage.dawn = 0.001;
  const tomorrow = new Date(Date.parse(TODAY + 'T12:00:00Z') + 86400000).toISOString().slice(0, 10);
  const tn = nightInfo(tomorrow);
  const text = shareText(totals);
  const row = (k, v) => h('div', { class: 'stat' }, h('span', {}, t(k)), h('b', {}, v));
  ui.replaceChildren(h('div', { class: 'end-screen' },
    h('h2', {}, t('end.title')),
    h('p', { class: 'end-line' }, run.reason === 'dawn' ? t('end.dawn') : t('end.tide')),
    // The night's window first: it is what the player made and keeps.
    app.lastNight ? h('button', { class: 'end-window', onclick: () => { resetShelf(); screens.showGlass(app.lastNight); } }, Object.assign(renderNight(app.lastNight, { ppc: 3 }), { className: 'end-window-canvas' })) : null,
    h('div', { class: 'stats' },
      row('nightGoal.' + run.plan.mode, goalCount(run.plan.mode, totals)),
      row('end.pools', `${totals.pools} / ${totals.poolsPlanned}`),
      row('end.tideLeft', t('hud.turns', { n: totals.tideLeft })),
      // Pen counts only mean something on 灯の囲い nights.
      run.plan.mode === 'pen' ? row('end.pens', String(totals.pens)) : null,
      run.plan.mode === 'pen' ? row('end.bestPen', String(totals.bestPen)) : null,
      totals.allFour ? row('end.four', String(totals.allFour)) : null),
    h('div', { class: 'end-actions' },
      beach ? h('button', { class: 'primary', onclick: () => { resetShelf(); screens.showBeach({ finds: beach, onDone: () => screens.showGlass(app.lastNight) }); } }, t('end.beach')) : null,
      h('button', { class: beach ? 'chip' : 'primary', onclick: () => { resetShelf(); screens.showGlass(app.lastNight); } }, t('end.glass')),
      h('button', { class: 'chip', onclick: async e => { try { await navigator.clipboard.writeText(text); e.target.textContent = t('end.copied'); } catch { /* denied */ } } }, t('end.share')),
      h('button', { class: 'chip', onclick: () => { resetShelf(); begin('practice'); } }, t('end.again')),
      h('button', { class: 'chip', onclick: () => { resetShelf(); app.titleSnapped = false; showTitle(); } }, t('end.title2'))),
    h('div', { class: 'tomorrow' }, moonIcon(tn.phase, 32), t('tomorrowGame', { tide: t('tide.' + tn.tide), game: t('mode.' + tn.mode) })),
  ));
}

// ------------------------------------------------------------------ input extras
canvas.addEventListener('pointerdown', () => { if (app.screen === 'resolve') app.speed = 3; });
addEventListener('keydown', e => {
  if (e.key === ' ' && app.screen === 'resolve') app.speed = 3;
  if (e.key === 'Backspace') undoAim();
});

// ------------------------------------------------------------------ loop
let last = performance.now(), slowFrames = 0, frameCount = 0;
function frame(dt) {
  const view = app.view;
  if (app.screen === 'enter' && view) {
    app.enterT -= dt;
    view.crabs.forEach((a, i) => {
      const e = a.entering; if (!e) return;
      e.t += dt;
      const k = Math.min(1, e.t / e.dur), ease = 1 - (1 - k) ** 2;
      const c = app.run.pool.crabs[i];
      view.place(a, c.x, c.y);
      a.group.position.z -= e.from * (1 - ease);
      a.group.position.y = -0.07 + Math.abs(Math.sin(e.t * 14)) * 0.04 * (1 - k);
      if (k >= 1) { a.entering = null; a.play('idle'); }
    });
    if (app.enterT <= 0) startTurn();
  }
  if (view?.playback) view.playback.speed = app.speed;
  if (cove) cove.update(dt, stage.camera);
  if (view && app.run?.pool.rules.rain && ['aim', 'thinking', 'resolve', 'enter', 'cleared'].includes(app.screen)) {
    const t0 = stage.rig.target;
    fx.rain(t0.x, t0.z, 20, 22, 6, view.water, view.now);
  }
  // The title is a composed still: keep the sky and moon sharp.
  stage.look.tilt = app.screen === 'title' ? 0 : 0.7;
  stage.look.range = app.screen === 'title' ? 400 : 7;
  stage.update(dt, !app.motion);
  if (LOOK === 'kagee') { updateKageeLanterns(stage); if (stage.frame) { stage.frame.group.visible = ['title', 'aim', 'thinking', 'resolve', 'enter', 'cleared'].includes(app.screen); layoutForeground(stage); } }
  view?.update(dt, stage.camera, !app.motion);
  if (app.screen === 'aim' || app.screen === 'thinking') view?.crabs.forEach(a => a.update(0, stage.camera));
  fx.update(dt, stage.camera, { w: innerWidth, h: innerHeight });
  stage.render();
}
function loop(now) {
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;
  if (!app.paused) frame(dt);
  // Auto quality: drop to low if the high tier can't hold ~40 fps.
  if (stage.quality === 'high' && !params.get('quality')) {
    frameCount++;
    if (dt > 1 / 40) slowFrames++;
    if (frameCount > 90) { if (slowFrames > 60) stage.setQuality('low'); frameCount = 0; slowFrames = 0; }
  }
  requestAnimationFrame(loop);
}

// Test hooks: advance presentation time without rAF, and lock aims directly.
Object.assign(window.__tsuki, {
  advance(seconds, step = 1 / 60) { for (let s = 0; s < seconds - 1e-9; s += step) frame(step); },
  pause(v = true) { app.paused = v; },
  lock(input) { aim.lockDirect({ crab: app.aimOrder[app.aimIndex], ...input }); },
  begin, showTitle, renderHud,
  // Jump straight to pool `i` of tonight (QA only).
  skipTo(i) { while (app.run.index < i) app.run = nextPool(app.run); openPool(false); },
});

// ------------------------------------------------------------------ boot
app.night = nightPlan(TODAY);
stage.setMoon(app.night.light, app.night.lunarDay);
ui.replaceChildren(h('div', { class: 'loading' }, moonIcon(app.night.phase, 40)));
await Promise.all([sprites.load(), stage.loadWorld(), bakedAttempts(TODAY).then(a => { app.attempts = a; })]);
if (LOOK === 'kagee') {
  applyKageeWorld(stage);
  for (const m of Object.values(stage.basinWater || {})) m.material.uniforms.kagee.value = 1;
  stage.sky.material.uniforms.moonSize.value = 2.2;
  addForegroundFrame(stage);
}
showTitle();
requestAnimationFrame(t0 => { last = t0; requestAnimationFrame(loop); });
